import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";
import { safeNext } from "@/lib/nav";
import { AuthLayout } from "@/pages/AuthLayout";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/nav";

function DemoAccounts({ onPick }) {
  const { data } = useQuery({ queryKey: ["login-config"], queryFn: () => get("/public/login-config"), staleTime: Infinity });
  if (!data?.demo_accounts?.length) return null;
  return (
    <div className="mt-6 space-y-2 rounded-[6px] border border-line bg-subtle p-4" data-tour="pt-demo-accounts-card" data-testid="demo-accounts-card">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">Demo accounts (preview only)</p>
      {data.demo_accounts.map((a) => (
        <button key={a.email} type="button" onClick={() => onPick(a)} data-testid={`demo-account-${a.role}`}
          className="tl-lift flex w-full items-center justify-between gap-3 rounded-[5px] border border-line bg-white px-3 py-2 text-left text-sm">
          <span><span className="block font-medium text-ink">{a.name}</span><span className="block text-xs text-ink-muted">{a.email}</span></span>
          <span className="rounded-full bg-brand-tint px-2 py-0.5 text-xs text-brand">{ROLE_LABELS[a.role]}</span>
        </button>
      ))}
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      nav(next, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your work email to continue to Onboard, the Legal Hiring Operating System."
      footer={<p data-testid="login-need-access">Need access? Contact your firm administrator.</p>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-tour="pt-login-email-input" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-brand hover:underline" data-testid="login-forgot-password-link">Forgot password</Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-tour="pt-login-password-input" data-testid="login-password-input" />
        </div>
        {error && <p className="rounded-lg bg-risk-tint px-3 py-2 text-sm text-risk" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-tour="pt-login-submit-btn" data-testid="login-submit-btn">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </Button>
      </form>
      <DemoAccounts onPick={(a) => { setEmail(a.email); setPassword(a.password); setError(""); }} />
    </AuthLayout>
  );
}
